import { globalPokemonDataStore } from '../OverworldAnimation.js';
import { moveSetManager } from './managers/MoveSetManager.js';
import { pokedexClassificationsDB } from './pokemonDB.js';
import { pokemonEvolutions, pokedexEntriesDB } from './pokemonDB.js';
import { env } from '../utils/env.js';
class PokemonDataLoader {
    constructor() {
        this.store = {};
        // Set to true during development to always fetch fresh data from API and overwrite localStorage
        // Set to false in production to leverage cached localStorage data
        this.overwriteLocalStorage = false;
    }

    async loadPokemonData() {
        try {
            // Check if data already exists in localStorage
            // const existingData = this.loadFromLocalStorage();
            // if (existingData) {
            //     this.store = existingData;
            //     logToOverlay(`Loaded ${Object.keys(existingData).length} Pokemon from localStorage (cached)`);
            //     return;
            // }

            // Data doesn't exist, load from CSV
            console.log('Loading Pokemon data from CSV...');
            const response = await fetch('/data/pokemon_data.csv');
            const csvText = await response.text();
            const lines = csvText.split('\n').filter((line) => line.trim() !== '');
            const headers = lines[0].split(',').map((header) => header.trim());

            const pokemonDatabase = {};

            for (let i = 1; i < lines.length; i++) {
                const row = lines[i].split(',').map((cell) => cell.trim());
                const pokemonData = {};
                headers.forEach((header, index) => {
                    if (header === 'hp' && i === 32) {
                        // TODO: Use a TSV instead of a CSV
                    }
                    pokemonData[header] = row[index];
                });

                const pokemonId = pokemonData.id || pokemonData.ID || i;
                // Add to the main database object
                pokemonDatabase[pokemonId] = pokemonData;
            }

            // Save the complete database to localStorage and memory
            this.store = pokemonDatabase;
            this.saveToLocalStorage(pokemonDatabase);

            console.log(
                `Pokemon data loading completed! Saved ${
                    Object.keys(pokemonDatabase).length
                } Pokemon to localStorage.`
            );
        } catch (error) {
            console.error(`Error loading Pokemon data: ${error.message}`);
        }
    }

    async loadPokemonDataFromAPI() {
        const API_BASE = env.VITE_API_BASE;

        try {
            // Check if data already exists in localStorage (only if not forcing overwrite)
            if (!this.overwriteLocalStorage) {
                const existingData = this.loadFromLocalStorage();
                if (existingData) {
                    this.store = existingData;
                    // Quick progress animation for localStorage load
                    const { animateQuickProgress } = await import('../OverworldAnimation.js');
                    await animateQuickProgress(
                        'POKEMON_DATA_API',
                        `Loaded ${Object.keys(existingData).length} Pokemon from cache`
                    );
                    return;
                }
            }

            // Data doesn't exist or overwriteLocalStorage is true, load from API
            const { updateLoadingProgress } = await import('../OverworldAnimation.js');
            updateLoadingProgress('POKEMON_DATA_API', 'Loading Pokemon data from API...');

            // Fetch all Pokemon data from game endpoint (returns full fields)
            const response = await fetch(`${API_BASE}/pokemon/game/`);

            if (!response.ok) {
                throw new Error(`Failed to fetch Pokemon data: ${response.status} ${response.statusText}`);
            }

            const apiPokemonArray = await response.json();

            if (!Array.isArray(apiPokemonArray)) {
                throw new Error('API response is not an array');
            }

            const pokemonDatabase = {};
            let loadedCount = 0;

            // Transform each Pokemon from API response to match CSV format
            apiPokemonArray.forEach((apiData) => {
                try {
                    // Transform API response to match CSV format
                    const pokemonData = {
                        id: apiData.id.toString(),
                        slug: apiData.slug,
                        name: apiData.name,
                        type1: apiData.type1,
                        type2: apiData.type2,
                        pokedex_entry: apiData.pokedex_entry,
                        pokedex_classification: apiData.pokedex_classification,
                        height: apiData.height?.toString() || '',
                        weight: apiData.weight?.toString() || '',
                        ability1Id: apiData.ability1Id?.toString() || '',
                        ability2Id: apiData.ability2Id?.toString() || '',
                        ability1_distribution: apiData.ability1_distribution?.toString() || '',
                        ability2_distribution: apiData.ability2_distribution?.toString() || '',
                        male_distribution: apiData.male_distribution?.toString() || '',
                        female_distribution: apiData.female_distribution?.toString() || '',
                        hp: apiData.hp?.toString() || '',
                        attack: apiData.attack?.toString() || '',
                        defense: apiData.defense?.toString() || '',
                        special_attack: apiData.special_attack?.toString() || '',
                        special_defense: apiData.special_defense?.toString() || '',
                        speed: apiData.speed?.toString() || '',
                        hp_yield: apiData.hp_yield?.toString() || '',
                        attack_yield: apiData.attack_yield?.toString() || '',
                        defense_yield: apiData.defense_yield?.toString() || '',
                        special_attack_yield: apiData.special_attack_yield?.toString() || '',
                        special_defense_yield: apiData.special_defense_yield?.toString() || '',
                        speed_yield: apiData.speed_yield?.toString() || '',
                        catch_rate: apiData.catch_rate?.toString() || '',
                        base_experience: apiData.base_experience?.toString() || '',
                        growth_rate: apiData.growth_rate || '',
                        base_friendship: apiData.base_friendship?.toString() || '',
                        egg_group_1: apiData.egg_group_1 || '',
                        egg_group_2: apiData.egg_group_2 || '',
                        egg_cycles: apiData.egg_cycles?.toString() || '',
                        createdAt: apiData.createdAt || '',
                        updatedAt: apiData.updatedAt || '',
                        backSprite: apiData.backSprite || '',
                        frontSprite: apiData.frontSprite || '',
                        miniSprite: apiData.miniSprite || '',
                        abilities: apiData.abilities || [],
                    };

                    const pokemonId = parseInt(pokemonData.id);
                    // Store with numeric key for consistent access
                    pokemonDatabase[pokemonId] = pokemonData;
                    loadedCount++;
                } catch (error) {
                    console.error(`Error processing Pokemon ${apiData.id || 'unknown'}: ${error.message}`);
                }
            });

            // Save the complete database to localStorage and memory
            this.store = pokemonDatabase;
            this.saveToLocalStorage(pokemonDatabase);

            console.log(`Pokemon data loading from API completed! Loaded ${loadedCount} Pokemon.`);
        } catch (error) {
            console.error(`Error loading Pokemon data from API: ${error.message}`);
        }
    }

    // Save the complete Pokemon database to localStorage
    saveToLocalStorage(pokemonDatabase) {
        try {
            localStorage.setItem('pokemon_data', JSON.stringify(pokemonDatabase));
            console.log('Pokemon database saved to localStorage');
        } catch (storageError) {
            console.error(`Failed to save Pokemon database to localStorage: ${storageError.message}`);
        }
    }

    // Load the complete Pokemon database from localStorage
    loadFromLocalStorage() {
        try {
            const storedData = localStorage.getItem('pokemon_data');
            if (storedData) {
                return JSON.parse(storedData);
            }
        } catch (error) {
            console.error(`Error loading Pokemon data from localStorage: ${error.message}`);
        }
        return null;
    }

    async loadPokemonDataIntoMemory() {
        try {
            const pokemonDataJSON = localStorage.getItem('pokemon_data');
            if (pokemonDataJSON === null || pokemonDataJSON === undefined) {
                console.error(`Error fetching pokemon data.`);
                return {};
            }

            // Parse JSON once outside the loop - major performance improvement
            const pokemonData = JSON.parse(pokemonDataJSON);

            for (let i = 1; i <= 386; i++) {
                try {
                    // Try both numeric and string keys since data might be stored either way
                    const currentPokemonData = pokemonData[i] || pokemonData[String(i)];

                    if (!currentPokemonData) {
                        console.warn(`No data found for Pokemon ID: ${i}`);
                        // Don't set empty object - let getRawPokemonDataV2 handle the error
                        continue;
                    }

                    const rawPokemonData = {};
                    rawPokemonData.id = parseInt(currentPokemonData.id);
                    rawPokemonData.name = currentPokemonData.name;
                    rawPokemonData.baseStats = {
                        attack: parseInt(currentPokemonData.attack),
                        defense: parseInt(currentPokemonData.defense),
                        specialAttack: parseInt(currentPokemonData.special_attack),
                        specialDefense: parseInt(currentPokemonData.special_defense),
                        speed: parseInt(currentPokemonData.speed),
                        maxHp: parseInt(currentPokemonData.hp),
                    };
                    rawPokemonData.classification = pokedexClassificationsDB[i]; // TODO: Update data
                    rawPokemonData.evYield = {
                        attack: parseInt(currentPokemonData.attack_yield),
                        defense: parseInt(currentPokemonData.defense_yield),
                        specialAttack: parseInt(currentPokemonData.special_attack_yield), // Fixed typo
                        specialDefense: parseInt(currentPokemonData.special_defense_yield), // Fixed typo
                        speed: parseInt(currentPokemonData.speed_yield),
                        hp: parseInt(currentPokemonData.hp_yield),
                        exp: parseInt(currentPokemonData.base_experience),
                    };
                    rawPokemonData.type1 = currentPokemonData.type1;
                    rawPokemonData.type2 = currentPokemonData.type2;

                    // TODO: Move away from this old moveSetManager approach
                    rawPokemonData.evolution = pokemonEvolutions[i]?.evolution; // Added safe access
                    rawPokemonData.pokedexEntry = pokedexEntriesDB[i]; // TODO: Update data
                    rawPokemonData.moveSetByLevelUp = {};
                    // Default empty movesets for all pokemon
                    for (let j = 1; j <= 100; j++) {
                        rawPokemonData.moveSetByLevelUp[j] = [];
                    }

                    let femaleDistribution = parseFloat(currentPokemonData.female_distribution);
                    let maleDistribution = parseFloat(currentPokemonData.male_distribution);

                    const defaultDistribution = {
                        female: 0.5,
                        male: 0.5,
                    };

                    if (!isNaN(femaleDistribution)) {
                        femaleDistribution = femaleDistribution / 100;
                    }
                    if (!isNaN(maleDistribution)) {
                        maleDistribution = maleDistribution / 100;
                    }

                    // Handle cases where one or both distributions are invalid
                    let finalFemaleDistribution, finalMaleDistribution;

                    if (isNaN(femaleDistribution) && isNaN(maleDistribution)) {
                        // Both are invalid - use default
                        finalFemaleDistribution = defaultDistribution.female;
                        finalMaleDistribution = defaultDistribution.male;
                    } else if (isNaN(femaleDistribution) && !isNaN(maleDistribution)) {
                        // Only female is invalid - calculate from male
                        finalMaleDistribution = maleDistribution;
                        finalFemaleDistribution = 1 - maleDistribution;
                    } else if (!isNaN(femaleDistribution) && isNaN(maleDistribution)) {
                        // Only male is invalid - calculate from female
                        finalFemaleDistribution = femaleDistribution;
                        finalMaleDistribution = 1 - femaleDistribution;
                    } else {
                        // Both are valid - check if they sum to 1.0 (with small tolerance for floating point precision)
                        const sum = femaleDistribution + maleDistribution;
                        const tolerance = 0.001;

                        if (Math.abs(sum - 1.0) <= tolerance) {
                            // Sum is close enough to 1.0 - use the parsed values
                            finalFemaleDistribution = femaleDistribution;
                            finalMaleDistribution = maleDistribution;
                        } else {
                            // Sum is not 1.0 - use default distribution
                            finalFemaleDistribution = defaultDistribution.female;
                            finalMaleDistribution = defaultDistribution.male;
                        }
                    }

                    rawPokemonData.genderDistribution = {
                        female: finalFemaleDistribution,
                        male: finalMaleDistribution,
                    };

                    rawPokemonData.abilities = currentPokemonData.abilities || [];

                    // Store with numeric key for consistent access
                    globalPokemonDataStore[i] = rawPokemonData;
                    // Also store with string key as fallback
                    globalPokemonDataStore[String(i)] = rawPokemonData;

                    // Optional: Yield control occasionally for large datasets
                    if (i % 50 === 0) {
                        await new Promise((resolve) => setTimeout(resolve, 0));
                    }
                } catch (error) {
                    console.error(`Error processing Pokemon ID ${i}:`, error);
                    // Don't set empty object - let getRawPokemonDataV2 handle the error
                }
            }

            return globalPokemonDataStore;
        } catch (error) {
            console.error('Error loading Pokemon data into memory:', error);
            return {};
        }
    }

    getDataForPokemon(id) {
        try {
            const pokemonDataJSON = localStorage.getItem('pokemon_data');
            const pokemonData = JSON.parse(pokemonDataJSON);
            const currentPokemonData = pokemonData[id];
            const rawPokemonData = {};
            rawPokemonData.id = parseInt(currentPokemonData.id);
            rawPokemonData.name = currentPokemonData.name;
            rawPokemonData.baseStats = {
                attack: parseInt(currentPokemonData.attack),
                defense: parseInt(currentPokemonData.defense),
                specialAttack: parseInt(currentPokemonData.specialAttack),
                specialDefense: parseInt(currentPokemonData.specialDefense),
                speed: parseInt(currentPokemonData.speed),
                maxHp: parseInt(currentPokemonData.hp),
            };
            rawPokemonData.classification = pokedexClassificationsDB[id]; // TODO: Update data
            rawPokemonData.evYield = {
                attack: parseInt(currentPokemonData.attack_yield),
                defense: parseInt(currentPokemonData.defense_yield),
                specialAttack: parseInt(currentPokemonData.defense_yield),
                specialDefense: parseInt(currentPokemonData.specia_defense_yield),
                speed: parseInt(currentPokemonData.speed_yield),
                hp: parseInt(currentPokemonData.hp_yield),
                exp: parseInt(currentPokemonData.base_experience),
            };
            rawPokemonData.type1 = currentPokemonData.type1;
            rawPokemonData.type2 = currentPokemonData.type2;
            const rawMoveSetData = moveSetManager.store[id];
            rawPokemonData.moveSetByLevelUp = rawMoveSetData.moveSetByLevelUp ?? {};
            rawPokemonData.moveSetByEgg = rawMoveSetData.moveSetByEgg ?? {};
            rawPokemonData.moveSetByTutor = rawMoveSetData.moveSetByTutor ?? {};
            rawPokemonData.moveSetByHM = rawMoveSetData.moveSetByHM ?? {};
            rawPokemonData.moveSetByTM = rawMoveSetData.moveSetByTM ?? {};
            rawPokemonData.evolution = pokemonEvolutions[id].evolution; // TODO: Update data
            rawPokemonData.pokedexEntry = pokedexEntriesDB[id]; // TODO: Update data

            let femaleDistribution = parseFloat(currentPokemonData.female_distribution);
            let maleDistribution = parseFloat(currentPokemonData.male_distribution);

            const defaultDistribution = {
                female: 0.5,
                male: 0.5,
            };

            if (!isNaN(femaleDistribution)) {
                femaleDistribution = femaleDistribution / 100;
            }
            if (!isNaN(maleDistribution)) {
                maleDistribution = maleDistribution / 100;
            }

            // Handle cases where one or both distributions are invalid
            let finalFemaleDistribution, finalMaleDistribution;

            if (isNaN(femaleDistribution) && isNaN(maleDistribution)) {
                // Both are invalid - use default
                finalFemaleDistribution = defaultDistribution.female;
                finalMaleDistribution = defaultDistribution.male;
            } else if (isNaN(femaleDistribution) && !isNaN(maleDistribution)) {
                // Only female is invalid - calculate from male
                finalMaleDistribution = maleDistribution;
                finalFemaleDistribution = 1 - maleDistribution;
            } else if (!isNaN(femaleDistribution) && isNaN(maleDistribution)) {
                // Only male is invalid - calculate from female
                finalFemaleDistribution = femaleDistribution;
                finalMaleDistribution = 1 - femaleDistribution;
            } else {
                // Both are valid - check if they sum to 1.0 (with small tolerance for floating point precision)
                const sum = femaleDistribution + maleDistribution;
                const tolerance = 0.001;

                if (Math.abs(sum - 1.0) <= tolerance) {
                    // Sum is close enough to 1.0 - use the parsed values
                    finalFemaleDistribution = femaleDistribution;
                    finalMaleDistribution = maleDistribution;
                } else {
                    // Sum is not 1.0 - use default distribution
                    finalFemaleDistribution = defaultDistribution.female;
                    finalMaleDistribution = defaultDistribution.male;
                }
            }

            rawPokemonData.genderDistribution = {
                female: finalFemaleDistribution,
                male: finalMaleDistribution,
            };
            return rawPokemonData;
        } catch (error) {
            console.error(error);
            return {};
        }
    }

    // Helper method to retrieve Pokemon data by ID
    getPokemonData(pokemonId) {
        return this.store[pokemonId] || null;
    }

    // Helper method to get a specific field for a Pokemon
    getPokemonField(pokemonId, fieldName) {
        const pokemonData = this.store[pokemonId];
        return pokemonData ? pokemonData[fieldName] : null;
    }

    // Helper method to get all Pokemon IDs
    getAllPokemonIds() {
        return Object.keys(this.store);
    }

    // Helper method to get all Pokemon data
    getAllPokemon() {
        return this.store;
    }

    // Helper method to clear all Pokemon data
    clearAllPokemonData() {
        try {
            localStorage.removeItem('pokemon_data');
            console.log('Cleared Pokemon data from localStorage');
        } catch (error) {
            console.error(`Error clearing localStorage: ${error.message}`);
        }

        // Clear memory store
        this.store = {};
    }
}

export const pokemonDataLoader = new PokemonDataLoader();
